import React from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import {
  CheckCircle2,
  Circle,
  MapPin,
  Camera,
  SplitSquareHorizontal,
  Hash,
  Layers
} from 'lucide-react';
import { PalletSummaryDto } from '../../../../types/picking';
import { PickingLine } from './types';

interface PickingLineItemProps {
  line: PickingLine;
  index: number;
  isSelected: boolean;
  isActive: boolean;
  pallets: PalletSummaryDto[];
  editingLocation: number | null;
  editingQuantity: number | null;
  newLocation: string;
  newQuantity: string;
  showPalletInfo: string | null;
  onLineClick: (index: number) => void;
  onLocationEdit: (lineNo: number, location: string) => void;
  onQuantityEdit: (lineNo: number, quantity: number) => void;
  onStartLocationEdit: (lineNo: number, location: string) => void;
  onStartQuantityEdit: (lineNo: number, quantity: string) => void;
  onLocationChange: (value: string) => void;
  onQuantityChange: (value: string) => void;
  onSplitLine: (lineNo: number) => void;
  onPickLine: (lineNo: number, qty: number) => void;
  onUndoPicking: (lineNo: number) => void;
  onShowPalletInfo: (palletId: string | null) => void;
}

export const PickingLineItem: React.FC<PickingLineItemProps> = ({
  line,
  index,
  isSelected,
  isActive,
  pallets,
  editingLocation,
  editingQuantity,
  newLocation,
  newQuantity,
  showPalletInfo,
  onLineClick,
  onLocationEdit,
  onQuantityEdit,
  onStartLocationEdit,
  onStartQuantityEdit,
  onLocationChange,
  onQuantityChange,
  onSplitLine,
  onPickLine,
  onUndoPicking,
  onShowPalletInfo
}) => {
  const { t } = useTranslation();
  const [showFullItemNo, setShowFullItemNo] = React.useState(false);
  const baseClasses = isActive
    ? 'border-blue-400 bg-blue-100 ring-1 ring-blue-200'
    : isSelected
    ? 'border-blue-500 bg-blue-50'
    : line.isFullyPicked
    ? 'border-green-400 bg-green-50 opacity-80'
    : line.isPartiallyPicked
    ? 'border-orange-400 bg-orange-50'
    : 'border-gray-200 hover:border-gray-300';

  return (
    <div
      className={`p-2 border rounded-lg cursor-pointer transition-colors ${baseClasses}`}
      onClick={() => onLineClick(index)}
    >
      {/* MOBILE: Stacked layout with tap-to-expand */}
      <div className="sm:hidden">
        {/* Row 1: Status icon + Item number (tap to expand) */}
        <div className="flex items-start gap-2 mb-1">
          <div className="flex-shrink-0 mt-0.5">
            {line.isFullyPicked ? (
              <CheckCircle2 className="text-green-600" size={16} />
            ) : line.isPartiallyPicked ? (
              <Circle className="text-orange-500 fill-current" size={16} />
            ) : (
              <Circle className="text-gray-400" size={16} />
            )}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowFullItemNo(!showFullItemNo);
            }}
            className="font-semibold text-xs text-gray-900 text-left flex-1 min-w-0 hover:text-blue-700 transition-colors"
          >
            {showFullItemNo ? (
              <span>{line.itemNo}</span>
            ) : (
              <span className="truncate block">{line.itemNo}</span>
            )}
          </button>

          {/* Icons and badges */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {line.image && <Camera className="text-blue-500" size={10} />}
            {line.serialNoItemYesNo && (
              <Hash className={`${(line.serialNumberCount || 0) >= line.qty ? 'text-green-500' : 'text-amber-500'}`} size={10} />
            )}
            {line.batchItemYesNo && line.itemType !== 4 && line.itemType !== 5 && (
              <Layers className={`${(line.batchNumberCount || 0) > 0 ? 'text-green-500' : 'text-amber-500'}`} size={10} />
            )}
          </div>
        </div>

        {/* Row 2: Description (tap to expand) */}
        {line.itemDescription && (
          <div className="mb-1 pl-6">
            <span className="text-xs text-gray-600 block">{line.itemDescription}</span>
          </div>
        )}

        {/* Row 3: Location + Quantity + Actions */}
        <div className="flex items-center gap-2 pl-6">
          <span className="text-xs text-gray-600">{line.location}</span>
          <span className="text-gray-400 text-xs">•</span>
          <span className={`text-xs font-medium ${line.isFullyPicked ? 'text-green-700' : line.isPartiallyPicked ? 'text-orange-700' : 'text-gray-700'}`}>
            {line.qtyPicked}/{line.qty}
          </span>

          {/* Pallet info */}
          {line.splitAllocations && line.splitAllocations.length > 0 && (
            <>
              <span className="text-gray-400 text-xs">•</span>
              <span className="bg-purple-100 text-purple-700 px-1 py-0.5 rounded text-[10px]">
                P{pallets.find(p => p.sscc === line.splitAllocations![0].palletId)?.sscc.slice(-4)}
              </span>
            </>
          )}

          {/* Actions - compact */}
          {((!line.requireItemScan && !line.requireLocationScan) || line.location === '[X]' || line.isCompleted) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (line.isCompleted) {
                  onUndoPicking(line.pListLineNo);
                } else {
                  onPickLine(line.pListLineNo, line.qty);
                }
              }}
              className={`ml-auto px-2 py-1 rounded text-[10px] font-medium min-h-[28px] ${
                line.isCompleted
                  ? 'bg-red-600 text-white'
                  : 'bg-blue-600 text-white'
              }`}
            >
              {line.isCompleted ? t('warehouse.modules.picking.actions.undo') : t('warehouse.modules.picking.pick')}
            </button>
          )}
        </div>
      </div>

      {/* DESKTOP: Full layout */}
      <div className="hidden sm:flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {/* Status icon */}
          <div className="flex-shrink-0 mt-0.5">
            {line.isFullyPicked ? (
              <CheckCircle2 className="text-green-600" size={18} />
            ) : line.isPartiallyPicked ? (
              <Circle className="text-orange-500 fill-current" size={18} />
            ) : (
              <Circle className="text-gray-400" size={18} />
            )}
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-0.5">
              <span className="font-bold text-gray-900 text-sm">{line.itemNo}</span>
              <span className="text-sm text-gray-700 truncate">{line.itemDescription}</span>
              {isActive && (
                <span className="bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium animate-pulse">
                  {t('warehouse.modules.picking.labels.active')}
                </span>
              )}
              {line.requireLocationScan && <MapPin className="text-orange-500" size={12} />}
              {line.image && <Camera className="text-blue-500" size={12} />}
              {line.serialNoItemYesNo && (
                <span className={`flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded ${
                  (line.serialNumberCount || 0) >= line.qty ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`} title="Krever serienummer">
                  <Hash size={10} />
                  {line.serialNumberCount || 0}/{Math.floor(line.qty)}
                </span>
              )}
              {line.batchItemYesNo && line.itemType !== 4 && line.itemType !== 5 && (
                <span className={`flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded ${
                  (line.batchNumberCount || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`} title="Krever batchnummer">
                  <Layers size={10} />
                  {line.batchNumberCount || 0}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>#{line.pListLineNo}</span>
              <span>•</span>
              <span>{t('warehouse.modules.picking.labels.ordered')}: {line.qty}</span>
              <span>•</span>
              <span>{t('warehouse.modules.picking.picked')}: {line.qtyPicked}</span>
              <span>•</span>
              <span>{line.location}</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {line.splitAllocations && line.splitAllocations.length > 1 ? (
            <div className="flex gap-1">
              {line.splitAllocations.map((allocation, idx) => {
                const pallet = pallets.find(p => p.sscc === allocation.palletId);
                return pallet ? (
                  <div
                    key={idx}
                    className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs cursor-pointer hover:bg-purple-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowPalletInfo(showPalletInfo === pallet.sscc ? null : pallet.sscc);
                    }}
                  >
                    P{pallet.sscc.slice(-4)}({allocation.qty})
                  </div>
                ) : null;
              })}
              <SplitSquareHorizontal className="text-purple-600 ml-0.5" size={14} />
            </div>
          ) : line.splitAllocations && line.splitAllocations.length === 1 ? (
            <div
              className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs cursor-pointer hover:bg-purple-200"
              onClick={(e) => {
                e.stopPropagation();
                const palletId = line.splitAllocations![0].palletId;
                onShowPalletInfo(showPalletInfo === palletId ? null : palletId);
              }}
            >
              P{pallets.find(p => p.sscc === line.splitAllocations![0].palletId)?.sscc.slice(-4)}
            </div>
          ) : null}

          <button
            onClick={(e) => {
              e.stopPropagation();
              onSplitLine(line.pListLineNo);
            }}
            className="text-gray-500 hover:text-gray-700 p-1"
            title={t('warehouse.modules.picking.actions.split')}
          >
            <SplitSquareHorizontal size={14} />
          </button>

          {((!line.requireItemScan && !line.requireLocationScan) || line.location === '[X]' || line.isCompleted) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (line.isCompleted) {
                  onUndoPicking(line.pListLineNo);
                } else {
                  onPickLine(line.pListLineNo, line.qty);
                }
              }}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                line.isCompleted
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {line.isCompleted ? t('warehouse.modules.picking.actions.undo') : t('warehouse.modules.picking.pick')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
